<script lang="ts">
  import { router, type Route } from '../router.svelte';
  import { auth } from '../firebase/auth.svelte';

  // Nav principale post-pivot : l'app EST l'Atelier (configurateur).
  // La boutique (catalogue, panier, commandes) vit sur Shopify — lien externe,
  // gouverné par VITE_SHOP_URL : non défini tant que le domaine n'est pas
  // réellement branché sur Shopify (Lot P0), pour ne pas exposer un lien mort.
  const links: Array<{ route: Route; label: string; href: string }> = [
    { route: 'home', label: 'Accueil', href: '#/' },
    { route: 'order', label: 'Créer mon kit', href: '#/order' },
    { route: 'editor', label: 'Éditeur', href: '#/editor' },
  ];
  const SHOP_URL = import.meta.env.VITE_SHOP_URL;

  let menuOpen = $state(false);
  let navOpen = $state(false);

  // Suivi du breakpoint mobile (burger + overlay, cf. modules/header.css).
  // Sert à n'appliquer `inert` sur la nav QUE quand elle est réellement un
  // overlay fermé : sur desktop la nav inline reste exposée aux lecteurs
  // d'écran et au clavier.
  let isMobileNav = $state(false);
  $effect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = (): void => {
      isMobileNav = mq.matches;
    };
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  });

  // Filet de sécurité : ferme l'overlay si la route change par un autre
  // chemin qu'un clic sur un lien (navigation programmatique). Le cas
  // nominal — clic sur un lien, y compris celui de la route déjà active —
  // est géré par le onclick des liens eux-mêmes.
  $effect(() => {
    void router.current;
    navOpen = false;
  });

  // Empêche le body de scroller pendant que l'overlay nav plein écran
  // est ouvert (sinon l'utilisateur peut « scroller derrière » le menu).
  $effect(() => {
    if (typeof document === 'undefined') return;
    if (navOpen) {
      document.body.classList.add('body--nav-open');
    } else {
      document.body.classList.remove('body--nav-open');
    }
  });

  function toggleMenu(): void {
    menuOpen = !menuOpen;
  }
  function closeMenu(): void {
    menuOpen = false;
  }
  async function handleSignOut(): Promise<void> {
    closeMenu();
    await auth.signOut();
  }

  let userInitial = $derived((auth.user?.email ?? '?').charAt(0).toUpperCase());
</script>

<header class="header">
  <div class="container header__inner">
    <!-- Burger : visible uniquement mobile, ouvre la nav full-screen -->
    <button
      type="button"
      class="header__burger"
      aria-label="Ouvrir le menu de navigation"
      aria-expanded={navOpen}
      onclick={() => (navOpen = true)}
    >
      ☰
    </button>

    <!-- Brand : à gauche sur desktop, centré sur mobile (position: absolute) -->
    <a href="#/" class="header__brand">Perleur</a>

    <!-- Nav : inline sur desktop, overlay plein écran sur mobile -->
    <nav
      class="header__nav"
      class:header__nav--open={navOpen}
      aria-label="Navigation principale"
      inert={isMobileNav && !navOpen}
    >
      <button
        type="button"
        class="header__nav-close"
        aria-label="Fermer le menu"
        onclick={() => (navOpen = false)}
      >
        ✕
      </button>
      {#each links as link (link.route)}
        <a
          href={link.href}
          aria-current={router.current === link.route ? 'page' : undefined}
          onclick={() => (navOpen = false)}
        >
          {link.label}
        </a>
      {/each}
      {#if auth.isAdmin}
        <a
          href="#/admin"
          aria-current={router.current === 'admin' ? 'page' : undefined}
          onclick={() => (navOpen = false)}
        >Admin</a>
      {/if}
      {#if SHOP_URL}
        <a
          href={SHOP_URL}
          target="_blank"
          rel="external noopener"
          onclick={() => (navOpen = false)}
        >Boutique</a>
      {/if}
    </nav>

    <div class="header__auth">
      {#if !auth.configured || auth.loading || !auth.user}
        <!-- Atelier public = pas de connexion visible. L'admin passe par la
             page dédiée non référencée /login. Rien à afficher ici. -->
      {:else}
        <!-- Connecté (admin) : trigger compact avec initiale + dropdown minimal. -->
        <button
          type="button"
          class="header__user-btn"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onclick={toggleMenu}
          title={auth.user.email ?? 'Mon compte'}
        >
          <span class="header__user-initial" aria-hidden="true">{userInitial}</span>
          {#if auth.isAdmin}
            <span class="header__user-name">Admin</span>
          {/if}
        </button>

        {#if menuOpen}
          <button
            type="button"
            class="header__menu-backdrop"
            aria-label="Fermer le menu"
            onclick={closeMenu}
          ></button>
          <div class="header__menu" role="menu">
            <div class="header__menu-info">
              <strong>{auth.isAdmin ? 'Administrateur' : 'Connecté'}</strong>
              <small>{auth.user.email ?? ''}</small>
            </div>
            <button
              type="button"
              role="menuitem"
              class="header__menu-item header__menu-item--danger"
              onclick={handleSignOut}
            >
              Se déconnecter
            </button>
          </div>
        {/if}
      {/if}
    </div>
  </div>
</header>

<style>
  .header__auth {
    position: relative;
    margin-left: auto;
  }
  .header__user-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-1) var(--space-3) var(--space-1) var(--space-1);
    background: transparent;
    border: 1px solid var(--color-border);
    border-radius: 999px;
    color: var(--color-text);
    font: inherit;
    font-size: var(--text-small);
    cursor: pointer;
    transition: background-color 0.12s ease;
  }
  .header__user-btn:hover {
    background: var(--color-surface);
  }
  .header__user-initial {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--color-primary);
    color: var(--color-background);
    font-size: var(--text-caption);
    font-weight: 700;
  }
  .header__user-name {
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* Menu déroulant */
  .header__menu-backdrop {
    position: fixed;
    inset: 0;
    background: transparent;
    border: 0;
    z-index: 50;
    cursor: default;
  }
  .header__menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    background: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    box-shadow: var(--shadow-md);
    min-width: 220px;
    z-index: 51;
    overflow: hidden;
  }
  .header__menu-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: var(--space-2) var(--space-3);
    border-bottom: 1px solid var(--color-border);
  }
  .header__menu-info strong {
    font-size: var(--text-small);
  }
  .header__menu-info small {
    font-size: var(--text-caption);
    color: var(--color-text-muted);
  }
  .header__menu-item {
    width: 100%;
    background: transparent;
    border: 0;
    text-align: left;
    padding: 10px var(--space-3);
    cursor: pointer;
    font: inherit;
    font-size: var(--text-small);
    color: var(--color-text);
  }
  .header__menu-item:hover {
    background: var(--color-surface);
  }
  .header__menu-item--danger {
    color: var(--color-danger);
  }
  /* Mobile : compacter le trigger (initiale seule, libellé caché) */
  @media (max-width: 640px) {
    .header__user-name {
      display: none;
    }
    .header__user-btn {
      padding-right: var(--space-1);
    }
  }
</style>
